/**
 * The account page (spec §8.27, M28): what belongs to the person rather than to a workspace. It holds the MCP
 * connector — its address, setup for common clients, and the person's tokens, each shown once on creation.
 */
import { clsx } from "clsx";
import { Check, Copy, KeyRound, Plug } from "lucide-react";
import { useState } from "react";
import { useMe, useWorkspaces } from "../api/hooks";
import { useI18n, useT } from "../i18n";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { claudeCodeCommand, connectorUrl, jsonConfig } from "./connector";
import { EXPIRY, REFUSED, RIGHTS } from "./labels";
import { TokenRefusal, useAccountTokens, useCreateToken, useRevokeToken, type McpToken } from "./api";

export function AccountPage() {
  const t = useT();
  const me = useMe();
  const account = useAccountTokens();
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<{ name: string; secret: string } | null>(null);
  const live = (account.data?.tokens ?? []).filter((tk) => !tk.revokedAt);
  const past = (account.data?.tokens ?? []).filter((tk) => tk.revokedAt);

  return (
    <div className="mx-auto w-full max-w-3xl px-9 pb-16 pt-8" data-testid="account-page">
      <h1 className="mb-1 text-[26px] font-semibold">{t("account.title")}</h1>
      <p className="mb-6 text-sm text-neutral-500">
        {me.data?.display_name || me.data?.username} · {me.data?.username}
      </p>

      <section className="kss-surface mb-4 p-4" data-testid="mcp-section">
        <h2 className="flex items-center gap-2 text-[17px] font-semibold">
          <Plug size={17} aria-hidden="true" />
          {t("account.mcp")}
        </h2>
        <p className="mb-3 text-[13px] text-neutral-500">{t("account.mcp.sub")}</p>
        {account.data && !account.data.enabled ? (
          <p className="rounded-sm bg-warn-tint px-3 py-2 text-sm text-warn" data-testid="mcp-disabled">
            {t("account.mcp.disabled")}
          </p>
        ) : (
          <>
            <div className="mb-4 text-sm">
              <span className="text-neutral-500">{t("account.mcp.address")}</span>{" "}
              <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-xs dark:bg-neutral-800" data-testid="mcp-url">
                {connectorUrl()}
              </code>
            </div>
            <div className="mb-2 flex items-center gap-3">
              <h3 className="grow text-sm font-semibold">{t("account.tokens")}</h3>
              <Button size="sm" onClick={() => setCreating(true)} disabled={live.length >= (account.data?.maxTokens ?? 0)} data-testid="mcp-token-new">
                <KeyRound size={14} aria-hidden="true" />
                {t("account.tokens.new")}
              </Button>
            </div>
            <TokenList tokens={live} />
            {live.length === 0 && account.data && <p className="py-2 text-sm text-neutral-500">{t("account.tokens.none")}</p>}
            {past.length > 0 && (
              <details className="mt-3 text-sm">
                <summary className="cursor-pointer text-neutral-500">{t("account.tokens.revoked", { count: past.length })}</summary>
                <TokenList tokens={past} />
              </details>
            )}
          </>
        )}
      </section>
      {account.isError && <p className="text-sm text-neutral-500">{t("server.needServer")}</p>}
      {creating && (
        <NewTokenDialog
          onClose={() => setCreating(false)}
          onCreated={(name, secret) => {
            setCreating(false);
            setCreated({ name, secret });
          }}
        />
      )}
      {created && <CreatedDialog name={created.name} secret={created.secret} onClose={() => setCreated(null)} />}
    </div>
  );
}

function TokenList({ tokens }: { tokens: McpToken[] }) {
  const t = useT();
  const { language } = useI18n();
  const revoke = useRevokeToken();
  const workspaces = useWorkspaces();
  const names = new Map((workspaces.data ?? []).map((w) => [w.id, w.name]));
  const date = (d: string | null | undefined) => (d ? new Date(d).toLocaleDateString(language) : "—");
  return (
    <ul className="divide-y divide-neutral-200 dark:divide-neutral-800" data-testid="mcp-token-list">
      {tokens.map((tk) => (
        <li key={tk.id} className={clsx("flex flex-wrap items-center gap-x-4 gap-y-1 py-2 text-sm", tk.revokedAt && "text-neutral-400")} data-testid="mcp-token" data-name={tk.name}>
          <span className="min-w-0 grow">
            <span className="font-medium">{tk.name}</span>{" "}
            <code className="font-mono text-xs text-neutral-500">{tk.prefix}…</code>
            <span className="block text-xs text-neutral-500">
              {t(RIGHTS[tk.rights] ?? tk.rights)} ·{" "}
              {tk.workspaces.length ? tk.workspaces.map((w) => names.get(w) ?? w).join(", ") : t("account.tokens.allWorkspaces")}
            </span>
          </span>
          <span className="text-xs text-neutral-500">
            {t("account.tokens.expires", { date: tk.expiresAt ? date(tk.expiresAt) : t("account.tokens.never") })}
            <br />
            {t("account.tokens.lastUsed", { date: tk.lastUsedAt ? date(tk.lastUsedAt) : t("account.tokens.notYet") })}
          </span>
          {!tk.revokedAt && (
            <Button size="sm" variant="danger" onClick={() => revoke.mutate(tk.id)} data-testid="mcp-token-revoke">
              {t("account.tokens.revoke")}
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}

function Modal({ title, onClose, children, testId }: { title: string; onClose(): void; children: React.ReactNode; testId: string }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-[rgba(28,27,26,0.36)] p-8" onClick={onClose}>
      <div
        className="w-full max-w-xl rounded-lg border border-neutral-200 bg-[var(--surface)] p-5 text-sm shadow-[var(--shadow-lg)] dark:border-neutral-700"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        role="dialog"
        aria-label={title}
        data-testid={testId}
      >
        <h2 className="mb-3 text-[17px] font-semibold">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function NewTokenDialog({ onClose, onCreated }: { onClose(): void; onCreated(name: string, secret: string): void }) {
  const t = useT();
  const me = useMe();
  const workspaces = useWorkspaces();
  const create = useCreateToken();
  const [name, setName] = useState("");
  const [rights, setRights] = useState<"read" | "write" | "admin">("read");
  const [all, setAll] = useState(true);
  const [chosen, setChosen] = useState<string[]>([]);
  const [expiry, setExpiry] = useState<"30d" | "90d" | "1y" | "never">("90d");
  const [error, setError] = useState<string | null>(null);
  const rightsOptions: Array<"read" | "write" | "admin"> = me.data?.isAdmin ? ["read", "write", "admin"] : ["read", "write"];
  return (
    <Modal title={t("account.tokens.new")} onClose={onClose} testId="mcp-token-dialog">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          create.mutate(
            { name, rights, workspaces: all ? [] : chosen, expiry },
            {
              onSuccess: (r) => onCreated(r.token.name, r.secret),
              onError: (err) => setError(err instanceof TokenRefusal ? t(REFUSED[err.code] ?? err.code) : String(err)),
            },
          );
        }}
      >
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-neutral-500">{t("account.tokens.name")}</span>
          <Input autoFocus required maxLength={100} value={name} placeholder={t("account.tokens.namePlaceholder")} onChange={(e) => setName(e.target.value)} data-testid="mcp-token-name" />
        </label>
        <fieldset className="mb-3">
          <legend className="mb-1 text-xs font-medium text-neutral-500">{t("account.tokens.rights")}</legend>
          {rightsOptions.map((r) => (
            <label key={r} className="flex items-center gap-2 py-0.5">
              <input type="radio" name="rights" checked={rights === r} onChange={() => setRights(r)} className="accent-[var(--color-accent)]" data-testid={`mcp-rights-${r}`} />
              {t(RIGHTS[r]!)}
            </label>
          ))}
        </fieldset>
        <fieldset className="mb-3">
          <legend className="mb-1 text-xs font-medium text-neutral-500">{t("account.tokens.workspaces")}</legend>
          <label className="flex items-center gap-2 py-0.5">
            <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} className="accent-[var(--color-accent)]" data-testid="mcp-all-workspaces" />
            {t("account.tokens.allWorkspacesLong")}
          </label>
          {!all &&
            (workspaces.data ?? []).map((w) => (
              <label key={w.id} className="ml-6 flex items-center gap-2 py-0.5">
                <input
                  type="checkbox"
                  checked={chosen.includes(w.id)}
                  onChange={(e) => setChosen(e.target.checked ? [...chosen, w.id] : chosen.filter((x) => x !== w.id))}
                  className="accent-[var(--color-accent)]"
                  data-testid="mcp-workspace"
                />
                {w.name}
              </label>
            ))}
        </fieldset>
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-neutral-500">{t("account.tokens.expiry")}</span>
          <select className="kss-control kss-field bg-transparent px-2 py-1 text-sm" value={expiry} onChange={(e) => setExpiry(e.target.value as typeof expiry)} data-testid="mcp-token-expiry">
            {(["30d", "90d", "1y", "never"] as const).map((x) => (
              <option key={x} value={x}>
                {t(EXPIRY[x]!)}
              </option>
            ))}
          </select>
        </label>
        {error && (
          <p className="mb-2 text-sm text-err" role="alert" data-testid="mcp-token-error">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={create.isPending || !name.trim() || (!all && chosen.length === 0)} data-testid="mcp-token-create">
            {t("account.tokens.create")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function CopyBlock({ text, testId }: { text: string; testId: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative mb-3">
      <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded bg-neutral-100 p-2 pr-10 font-mono text-xs dark:bg-neutral-800" data-testid={testId}>
        {text}
      </pre>
      <button
        type="button"
        className="absolute right-1.5 top-1.5 rounded p-1 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-900 dark:hover:bg-neutral-700 dark:hover:text-neutral-100"
        aria-label={t("account.copy")}
        title={t("account.copy")}
        onClick={() =>
          void navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          })
        }
        data-testid={`${testId}-copy`}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
}

function CreatedDialog({ name, secret, onClose }: { name: string; secret: string; onClose(): void }) {
  const t = useT();
  return (
    <Modal title={t("account.created.title", { name })} onClose={onClose} testId="mcp-token-created">
      <p className="mb-2 rounded-sm bg-warn-tint px-3 py-2 text-warn">{t("account.created.once")}</p>
      <CopyBlock text={secret} testId="mcp-token-secret" />
      <h3 className="mb-1 text-xs font-medium text-neutral-500">{t("account.created.claudeCode")}</h3>
      <CopyBlock text={claudeCodeCommand(secret)} testId="mcp-snippet-cli" />
      <h3 className="mb-1 text-xs font-medium text-neutral-500">{t("account.created.json")}</h3>
      <CopyBlock text={jsonConfig(secret)} testId="mcp-snippet-json" />
      <div className="flex justify-end">
        <Button onClick={onClose} data-testid="mcp-token-done">
          {t("account.created.done")}
        </Button>
      </div>
    </Modal>
  );
}
